import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { getStorage, saveQuickDialItems, saveBookmarks, getApiBase, resolveImageUrl } from '@/utils/storage';
import { QuickDialItem } from '@/types';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Edit, Trash2, X, Plus, Upload } from 'lucide-react';
import ImageWithLoader from '@/components/ImageWithLoader';

const SortableItem = ({ item, onEdit, handleItemClick, formatVisitCount, isReadOnly }: { item: QuickDialItem, onEdit: (i: QuickDialItem) => void, handleItemClick: (e: React.MouseEvent, i: QuickDialItem) => void, formatVisitCount: (n?: number) => string, isReadOnly?: boolean }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: item.id }); // Removed disabled: isReadOnly to allow sorting

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="relative group p-[2px]">
       <a
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        className="block aspect-video bg-white dark:bg-slate-800 rounded-xl overflow-hidden shadow-sm hover:shadow-lg dark:hover:shadow-[0_0_15px_rgba(59,130,246,0.2)] group-hover:scale-[1.01] transition-all relative z-10"
        onClick={(e) => {
            if (isDragging) e.preventDefault();
            else handleItemClick(e, item);
        }}
      >
        <div className="w-full h-full flex items-center justify-center relative flex-col">
            {/* Background Gradient/Image */}
            {item.thumbnail ? (
                 <ImageWithLoader 
                    src={item.thumbnail} 
                    alt="" 
                    className="absolute inset-0 w-full h-full" 
                    placeholderClassName="bg-slate-200 dark:bg-slate-800"
                    style={{ objectFit: 'cover' }} // Ensure cover fit
                 />
            ) : (
                 // No placeholder icon or background if no thumbnail, just transparent
                 null
            )}
            
            {/* Visit Count Badge - Always visible if count > 0 */}
            {item.visitCount && item.visitCount > 0 && (
                <div className="absolute bottom-2 right-2 z-20 px-1.5 py-0.5 bg-black/40 backdrop-blur-sm rounded-md text-[10px] text-white/80 font-mono">
                    {formatVisitCount(item.visitCount)}
                </div>
            )}
            
            {/* Icon/Content - Initially Hidden, Show on Hover */}
            {item.favicon && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300 bg-black/20 backdrop-blur-[2px] z-10">
                    <div className="w-10 h-10 bg-white/90 dark:bg-slate-800/90 rounded-full flex items-center justify-center mb-1 overflow-hidden shadow-sm dark:shadow-[0_0_10px_rgba(255,255,255,0.1)] transform translate-y-2 group-hover:translate-y-0 transition-transform">
                        <ImageWithLoader src={item.favicon} alt="" className="w-6 h-6" />
                    </div>
                    <span className="text-white text-xs font-medium px-2 text-center bg-black/50 rounded-full py-0.5 backdrop-blur-sm transform translate-y-2 group-hover:translate-y-0 transition-transform delay-75">
                        {item.title}
                    </span>
                </div>
            )}
            {!item.favicon && (
                 <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 bg-black/20 backdrop-blur-[2px]">
                    <span className="text-white text-sm font-medium px-3 py-1 bg-black/50 rounded-full backdrop-blur-sm">
                        {item.title}
                    </span>
                 </div>
            )}
        </div>
      </a>
      
      {/* White background layer with slight expansion and shadow - Always visible but enhanced on hover */}
      <div className="absolute inset-0 bg-white dark:bg-slate-800 rounded-xl shadow-sm -z-0 scale-[1.02] transition-all duration-300 group-hover:shadow-md group-hover:scale-[1.03]"></div>

      {!isReadOnly && (
          <button
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onEdit(item);
            }}
            className="absolute top-2 right-2 p-1.5 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-white/10 opacity-0 group-hover:opacity-100 transition-opacity text-slate-500 hover:text-blue-500 dark:hover:text-blue-400 z-20 shadow-md"
        >
            <Edit size={14} />
        </button>
      )}
    </div>
  );
};

interface EditModalProps {
    item: QuickDialItem | null;
    isOpen: boolean;
    onClose: () => void;
    onSave: (item: QuickDialItem) => void;
    onDelete: (id: string) => void;
}

const EditModal = ({ item, isOpen, onClose, onSave, onDelete }: EditModalProps) => {
    const [formData, setFormData] = useState<QuickDialItem>({ id: '', title: '', url: '', favicon: '', thumbnail: '' });

    useEffect(() => {
        if (item) {
            setFormData(item);
        } else {
            setFormData({ id: Date.now().toString(), title: '', url: '', favicon: '', thumbnail: '' });
        }
    }, [item]);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, field: 'favicon' | 'thumbnail') => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) { // Limit to 2MB
                alert('图片大小不能超过 2MB');
                return;
            }
            
            const formData = new FormData();
            formData.append('file', file);

            try {
                const apiBase = getApiBase();
                // Extract base URL from apiBase (remove /api)
                const baseUrl = apiBase.replace(/\/api$/, '');
                const response = await fetch(`${baseUrl}/upload`, {
                    method: 'POST',
                    body: formData
                });
                
                if (!response.ok) throw new Error('Upload failed');
                
                const data = await response.json();
                setFormData(prev => ({ ...prev, [field]: data.url }));
            } catch (error) {
                console.error('Upload error:', error);
                alert('上传失败，请确保本地服务已启动 (运行 start-server.bat)');
            }
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 p-6 rounded-2xl w-full max-w-md shadow-2xl">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-slate-900 dark:text-white">{item ? '编辑常用网站' : '添加常用网站'}</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-900 dark:hover:text-white"><X size={20} /></button>
                </div>
                
                <div className="space-y-4">
                    <div>
                        <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">标题</label>
                        <input 
                            type="text" 
                            value={formData.title}
                            onChange={e => setFormData({...formData, title: e.target.value})}
                            className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-slate-900 dark:text-white outline-none focus:border-blue-500 dark:focus:border-blue-400"
                        />
                    </div>
                    <div>
                        <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">URL</label>
                        <input 
                            type="text" 
                            value={formData.url}
                            onChange={e => setFormData({...formData, url: e.target.value})}
                            className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-slate-900 dark:text-white outline-none focus:border-blue-500 dark:focus:border-blue-400"
                        />
                    </div>
                    <div>
                        <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">图标 URL (可选)</label>
                        <div className="flex gap-2">
                            <input 
                                type="text" 
                                value={formData.favicon}
                                onChange={e => setFormData({...formData, favicon: e.target.value})}
                                className="flex-1 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-slate-900 dark:text-white outline-none focus:border-blue-500 dark:focus:border-blue-400"
                                placeholder="留空自动获取或上传"
                            />
                            <label className="cursor-pointer flex items-center justify-center px-3 py-2 bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg hover:bg-slate-200 dark:hover:bg-white/10 transition-colors" title="上传图标">
                                <Upload size={16} className="text-slate-500 dark:text-slate-400" />
                                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFileChange(e, 'favicon')} />
                            </label>
                        </div>
                    </div>
                     <div>
                        <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">缩略图 URL (可选，作为背景)</label>
                        <div className="flex gap-2">
                            <input 
                                type="text" 
                                value={formData.thumbnail}
                                onChange={e => setFormData({...formData, thumbnail: e.target.value})}
                                className="flex-1 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-slate-900 dark:text-white outline-none focus:border-blue-500 dark:focus:border-blue-400"
                                placeholder="图片 URL 或上传"
                            />
                             <label className="cursor-pointer flex items-center justify-center px-3 py-2 bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg hover:bg-slate-200 dark:hover:bg-white/10 transition-colors" title="上传缩略图">
                                <Upload size={16} className="text-slate-500 dark:text-slate-400" />
                                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFileChange(e, 'thumbnail')} />
                            </label>
                        </div>
                    </div>
                </div>

                <div className="flex justify-between mt-6">
                    {item && (
                        <button 
                            onClick={() => onDelete(item.id)}
                            className="text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 text-sm flex items-center gap-1"
                        >
                            <Trash2 size={16} /> 删除
                        </button>
                    )}
                    <div className="flex gap-2 ml-auto">
                        <button onClick={onClose} className="px-4 py-2 text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white text-sm">取消</button>
                        <button 
                            onClick={() => onSave(formData)}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm"
                        >
                            保存
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

interface QuickDialProps {
    sourceTagId?: string;
}

const QuickDial = ({ sourceTagId }: QuickDialProps) => {
  const [items, setItems] = useState<QuickDialItem[]>([]);
  const [editingItem, setEditingItem] = useState<QuickDialItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    const loadData = async () => {
      const data = await getStorage();
      if (sourceTagId) {
          const taggedBookmarks = data.bookmarks.filter(b => b.tags.includes(sourceTagId));
          const mappedItems: QuickDialItem[] = taggedBookmarks.map(b => ({
              id: b.id,
              title: b.title,
              url: b.url,
              favicon: b.favicon,
              thumbnail: b.thumbnail || '', // Use bookmark thumbnail if available
              visitCount: b.visitCount
          }));
          setItems(mappedItems);
      } else {
          setItems(data.settings.quickDialItems);
      }
    };
    loadData();
  }, [sourceTagId]);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
        // Optimistic update
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        setItems(newItems);

        if (sourceTagId) {
            // Update Bookmarks
            const data = await getStorage();
            const allBookmarks = data.bookmarks;
            const globalOldIndex = allBookmarks.findIndex(b => b.id === active.id);
            const globalNewIndex = allBookmarks.findIndex(b => b.id === over.id);
            
            if (globalOldIndex !== -1 && globalNewIndex !== -1) {
                const newBookmarks = arrayMove(allBookmarks, globalOldIndex, globalNewIndex);
                await saveBookmarks(newBookmarks);
            }
        } else {
            // Update QuickDialItems
            await saveQuickDialItems(newItems);
        }
    }
  };

  const handleSave = async (newItem: QuickDialItem) => {
      if (sourceTagId) return;
      
      let newItems;
      if (editingItem) {
          // Edit existing
          newItems = items.map(i => i.id === newItem.id ? { ...newItem, visitCount: i.visitCount } : i);
      } else {
          // Add new
          newItems = [...items, { ...newItem, visitCount: 0 }];
      }
      
      setItems(newItems);
      await saveQuickDialItems(newItems);
      setIsModalOpen(false);
      setEditingItem(null); // Clear editing state
  };

  const handleDelete = async (id: string) => {
      if (sourceTagId) return;
      const newItems = items.filter(i => i.id !== id);
      setItems(newItems);
      await saveQuickDialItems(newItems);
      setIsModalOpen(false);
      setEditingItem(null);
  };

  const formatVisitCount = (count?: number) => {
      if (!count) return '';
      if (count >= 1000) {
          return (count / 1000).toFixed(1) + 'k';
      }
      return count.toString();
  };

  const handleItemClick = (e: React.MouseEvent, item: QuickDialItem) => {
      // Update visit count
      // If linked to bookmarks, this won't persist to quickDialItems.
      // We should ideally update the bookmark's visit count, but getStorage doesn't expose a direct updateBookmark method easily here without more refactoring.
      // For now, optimistic update only in view if read-only, or persist if not.
      
      const newItems = items.map(i => {
          if (i.id === item.id) {
              return { ...i, visitCount: (i.visitCount || 0) + 1 };
          }
          return i;
      });
      setItems(newItems); // Optimistic update
      
      if (!sourceTagId) {
          saveQuickDialItems(newItems);
      }
  };
  if (items.length === 0) {
      if (sourceTagId) return null; // Hide if empty and linked
      
      return (
        <div className="text-center py-8">
            <button 
                onClick={() => { setEditingItem(null); setIsModalOpen(true); }}
                className="px-4 py-2 bg-white/50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white dark:hover:bg-white/10 transition-colors inline-flex items-center gap-2"
            >
                <Plus size={18} /> 添加常用网站
            </button>
                <EditModal 
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    item={editingItem}
                    onSave={handleSave}
                    onDelete={handleDelete}
                />
        </div>
      );
  }

  return (
    <div className="w-full max-w-5xl mx-auto">
      <DndContext 
        sensors={sensors} 
        collisionDetection={closestCenter} 
        onDragEnd={handleDragEnd}
      >
        <SortableContext 
          items={items.map(i => i.id)} 
          strategy={rectSortingStrategy}
        >
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {items.map((item) => (
              <SortableItem 
                key={item.id} 
                item={item} 
                onEdit={(i) => { setEditingItem(i); setIsModalOpen(true); }}
                handleItemClick={handleItemClick}
                formatVisitCount={formatVisitCount}
                isReadOnly={!!sourceTagId} // This actually only controls the edit button visibility now
              />
            ))}
             {!sourceTagId && (
                 <button 
                    onClick={() => { setEditingItem(null); setIsModalOpen(true); }}
                    className="aspect-video bg-white/50 dark:bg-white/5 border border-slate-200 dark:border-white/10 border-dashed rounded-xl flex items-center justify-center text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-white hover:bg-white/80 dark:hover:bg-white/10 hover:border-solid transition-all"
                >
                    <Plus size={32} />
                </button>
             )}
          </div>
        </SortableContext>
      </DndContext>

      {!sourceTagId && (
          <EditModal 
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            item={editingItem}
            onSave={handleSave}
            onDelete={handleDelete}
        />
      )}
    </div>
  );
};

export default QuickDial;
